/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import { withMyTheme } from '../theme/theme';
import { CircularProgress } from '@mui/material';
import { ImageResource } from '../resources/ImageResource';
import { useIsMobile } from '../util/useIsMobile';

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    visibility: hidden;
  }
`;

const OverlayStyle = withMyTheme((theme) => css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${theme.palette.background.default};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: opacity 0.5s ease-in-out;
  
  &.loaded {
    animation: ${fadeOut} 0.8s forwards;
  }
`);

const LogoStyle = withMyTheme(() => css`
  width: 150px;
  height: auto;
  margin-bottom: 2rem;
  opacity: 0.8;
`);

const LoaderContainerStyle = withMyTheme(() => css`
  display: flex;
  flex-direction: column;
  align-items: center;
`);

const ProgressStyle = withMyTheme((theme) => css`
  color: ${theme.palette.primary.main};
`);

const LoadingTextStyle = withMyTheme((theme) => css`
  margin-top: 1rem;
  font-family: ${theme.typography.body1.fontFamily};
  color: ${theme.palette.text.secondary};
  font-size: 1rem;
  letter-spacing: 2px;
`);

export const LoadingOverlay: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isMobile = useIsMobile();
  const imageToLoad = isMobile ? ImageResource.startBackgroundMobile : ImageResource.startBackground;
  
  useEffect(() => {
    const img = new Image();
    img.src = imageToLoad;
    
    const handleImageLoaded = () => {
      // Add a small delay to ensure smooth transition
      setTimeout(() => {
        setIsLoaded(true);
      }, 500);
    };
    
    if (img.complete) {
      handleImageLoaded();
    } else {
      img.addEventListener('load', handleImageLoaded);
    }
    
    return () => {
      img.removeEventListener('load', handleImageLoaded);
    };
  }, [imageToLoad]);
  
  return (
    <div css={OverlayStyle} className={isLoaded ? 'loaded' : ''}>
      <img src={ImageResource.logo} alt="Weddie Site" css={LogoStyle} />
      <div css={LoaderContainerStyle}>
        <CircularProgress size={60} thickness={4} css={ProgressStyle} />
        <div css={LoadingTextStyle}>LOADING</div>
      </div>
    </div>
  );
};
